import { Injectable } from '@angular/core';
import { UpgradeModule } from '@angular/upgrade/static';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { EdiSetupModalComponent } from '@app/modal/edi-setup-modal/edi-setup-modal.component';
import { GridRow } from '@app/core/components/grid/grid.types';
import { DataTaskService } from '@app/core/services/data-task.service';
import { Base64UrlService } from '@app/services/base64url.service';

@Injectable({ providedIn: 'root' })
export class EdiService {
    constructor(private readonly datatask: DataTaskService, private readonly base64: Base64UrlService,
        private readonly upgrade: UpgradeModule, private readonly dialog: MatDialog) {}

    /** @datatask 3668 */
    public async getOrderHeads(params: EdiParams): Promise<EdiOrderHeadsGet> { return (await this.datatask.Post(3668, params))[0]; }

    /** @datatask 3669 */
    public getStatuses(): Promise<EdiOrderStatus[]> { return this.datatask.Post(3669, {}); }

    /** @datatask 2266 */
    public async getSetup(params: EdiParams): Promise<EdiSetupGet> {
        const page: EdiSetupGet = await this.datatask.Post(2266, params);
        const decode = (item: EdiNamedItem, fields: (keyof EdiNamedItem)[]) => {
            for (const field of fields) {
                const value = item[field];
                if (typeof value !== 'string' || !value) continue;
                try { Object.assign(item, { [field]: this.base64.urlDecode(value) }); } catch { /* Keep undecodable legacy values. */ }
            }
        };
        for (const destination of Object.values(page.ediDestinations ?? {})) decode(destination, ['name']);
        for (const type of Object.values(page.ediTypes ?? {})) {
            decode(type, ['name']);
            for (const procedure of Object.values(type.exportprocs ?? {})) decode(procedure, ['name']);
            for (const report of Object.values(type.reportdefs ?? {})) decode(report, ['name', 'description', 'filename']);
        }
        return page;
    }

    public async openSetupEditor(parameters: EdiSetupEditorParams): Promise<EdiSetupEditorResult | undefined> {
        const stagedRow = { ...parameters.dataItem, dirtyFields: { ...parameters.dataItem.dirtyFields } };
        const instance = this.dialog.open<EdiSetupModalComponent, EdiSetupModalData, EdiSetupEditorResult>(EdiSetupModalComponent, {
            width: '95vw', maxWidth: '95vw', disableClose: true,
            data: { ...parameters, dataItem: stagedRow, validate: row => this.validateSetup(row) },
        });
        const response = await firstValueFrom(instance.afterClosed());
        if (response && !response.delete) Object.assign(parameters.dataItem, stagedRow);
        return response;
    }

    public async validateSetup(row: EdiSetupRow): Promise<EdiSetupValidationResult> {
        // Reuse the shared dynamic validator, including its follow-up dialogs.
        const validation: EdiDynamicValidator = this.upgrade.$injector.get('validationService');
        return await validation.dynamicValidationModal(2604, row);
    }
}

export interface EdiParams { argtype: string; argvalue: string; }
export interface EdiOrderHeadsGet {
    ordersearch: string;
    status_id: string;
    date_fom: string;
    date_tom: string;
    dateselector_index: string;
    date_fomtom?: string;
    selected_edi_orderhead_keyno: string | number;
    selected_edi_orderline_keyno: string | number;
}
export interface EdiOrderStatus { orderstatus_id: string | number; orderstatus_name: string; }
export interface EdiOrderRow extends GridRow { edi_orderhead_keyno?: string | number; edi_orderline_keyno?: string | number; }
export interface EdiNamedItem { keyno: number; name: string; description?: string; filename?: string; }
export interface EdiType extends EdiNamedItem { reportdefs?: EdiNamedItem[]; exportprocs?: EdiNamedItem[]; }
export interface EdiSetupGet { ediTypes?: Record<string, EdiType>; ediDestinations?: EdiNamedItem[]; }
export interface EdiSetupRow extends GridRow {
    custNo?: number;
    businesscoNo?: number;
    custgrpId?: string;
    sendByEmail?: boolean | string;
    sendEdi?: boolean | string;
    dirty?: boolean;
    remarks?: string;
    p2EdisetupKeyno?: number;
    ediTypeKeyno?: number;
    p2ReportdefKeyno?: number;
    p2ExportprocKeyno?: number;
    ediDestinationKeyno?: number;
    dirtyFields?: Record<string, boolean>;
}
export interface EdiSetupEditorParams extends EdiParams { dataItem: EdiSetupRow; data: EdiSetupGet; rows: EdiSetupRow[]; }
export interface EdiSetupEditorResult { delete: boolean; }
export interface EdiSetupEditGet extends EdiSetupRow {
    p2EdisetupKeyno: number;
    custNo: number;
    businesscoNo: number;
    custgrpId: string;
    ediTypeKeyno: number;
    p2ReportdefKeyno: number;
    p2ExportprocKeyno: number;
    ediDestinationKeyno: number;
    sendByEmail: string;
    sendEdi: string;
    remarks: string;
}
export interface EdiSetupValidationResult { haserror?: boolean; iserror?: boolean; }
export interface EdiSetupModalData extends EdiSetupEditorParams {
    validate(row: EdiSetupRow): Promise<EdiSetupValidationResult>;
}
interface EdiDynamicValidator {
    dynamicValidationModal(method: number, row: EdiSetupRow): PromiseLike<EdiSetupValidationResult>;
}
